/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { ActionGroup, ActionType } from 'client-code/actions';
import { PreviewGroup, ScreenType } from 'client-code/previews';

import { APPLICATION_FEATURE_AREA } from '../../../config';
import { LiveTvError } from '../../error';
import { NdvrInteractor, ServicesClientImpl } from '../../interactors';
import { IRecordingPreviewParams } from '../../interfaces/IPreviewService';
import {
    TActionList,
    TActionsContentParams,
    TActionsRecordingParams,
    TActionsUnifiedCollectionParams,
    TOfferPreview,
} from '../../types';
import { UniqueIdentifier } from '../../utils';
import { MetadataType, NdvrPlaybackType } from '../enums';
import { IMetadata, IMetadataModel, INdvrPlaybackModel } from '../interfaces';

export class NdvrMetaDataModelImpl implements IMetadataModel {
    private static TAG: string = 'NdvrMetadataModelImpl';

    private mError: any = null;

    private mMetadata: IMetadata = {};

    private mRecordingId: string = '';

    private mServices = new ServicesClientImpl();

    private async deleteRecording(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            try {
                this.mServices.getRequestModelToDeleteRecordingSchedule(this.mRecordingId).send();
                resolve();
            } catch (error) {
                this.mError = error;
                reject(error);
            }
        });
    }

    private async retrieveActionList(ndvrPlaybackModel: INdvrPlaybackModel): Promise<void> {
        switch (ndvrPlaybackModel.ndvrType) {
            case NdvrPlaybackType.RECORDING:
                this.mRecordingId = ndvrPlaybackModel.id;
                return Promise.resolve();
            case NdvrPlaybackType.UNIFIED_COLLECTION:
                return this.fetchUnifiedCollectionAction(ndvrPlaybackModel.id);
            case NdvrPlaybackType.CONTENT:
                return this.fetchContentActions(ndvrPlaybackModel.id);
            default:
        }
    }

    private async fetchRecordingAction(): Promise<void> {
        const networkCallId = UniqueIdentifier.getNextActionsNetworkCallIdForLiveTv();
        const requestParams: TActionsRecordingParams = {
            recordingId: this.mRecordingId,
            groups: [
                ActionGroup.Watch,
                ActionGroup.ScheduleRecording,
                ActionGroup.ScheduleOnepass,
                ActionGroup.ScheduleBookmark,
            ],
            screenType: APPLICATION_FEATURE_AREA.PLAYER,
            actionsNetworkCallId: networkCallId,
        };
        return new Promise((resolve, reject) => {
            this.mServices
                .getRecordingActionsV2(requestParams)
                .then((response: TActionList) => {
                    this.mMetadata.actionList = response.actionList;
                    resolve();
                })
                .catch((error) => {
                    this.mError = error;
                    reject(error);
                });
        });
    }

    private async fetchRecordingLastViewedPosition(): Promise<void> {
        try {
            const response = await new NdvrInteractor()
                .getRequestModelToFetchRecordingsLastViewedPosition(this.mRecordingId)
                .send();
            this.mMetadata.ndvrLastViewedPosition = response.data.lastViewedPosition;
        } catch (error) {
            this.mError = error;
            this.mMetadata.ndvrLastViewedPosition = 0;
        }
    }

    private async fetchContentActions(contentId: string): Promise<void> {
        const requestParams: TActionsContentParams = {
            contentId,
            groups: [ActionGroup.Watch],
            screenType: APPLICATION_FEATURE_AREA.PLAYER,
        };
        return new Promise((resolve, reject) => {
            this.mServices
                .getContentActions(requestParams)
                .then((response: TActionList) => {
                    this.mMetadata.actionList = response.actionList;
                    const watchAction = this.mMetadata.actionList.actions.find(
                        (action) => action.actionType === ActionType.WatchNowAction,
                    );
                    if (watchAction) this.mRecordingId = watchAction.kernel.recordingId;
                    resolve();
                })
                .catch((error) => {
                    this.mError = error;
                    reject(error);
                });
        });
    }

    private async fetchUnifiedCollectionAction(unifiedCollectionId: string): Promise<void> {
        const requestParams: TActionsUnifiedCollectionParams = {
            unifiedCollectionId,
            groups: [ActionGroup.Watch],
            screenType: APPLICATION_FEATURE_AREA.PLAYER,
        };
        return new Promise((resolve, reject) => {
            this.mServices
                .getUnifiedCollectionActions(requestParams)
                .then((response: TActionList) => {
                    this.mMetadata.actionList = response.actionList;
                    const watchAction = this.mMetadata.actionList.actions.find(
                        (action) => action.actionType === ActionType.WatchUnifiedCollectionAction,
                    );
                    if (watchAction) {
                        this.mRecordingId = watchAction.kernel.recordingId;
                    }
                    resolve();
                })
                .catch((error) => {
                    this.mError = error;
                    reject(error);
                });
        });
    }

    private async fetchRecordingOfferPreviewData(screenType: ScreenType): Promise<void> {
        let groups: PreviewGroup[] = [];
        if (screenType === ScreenType.FullBanner) {
            groups = [
                PreviewGroup.Metadata,
                PreviewGroup.Linear,
                PreviewGroup.CriticRating,
                PreviewGroup.Description,
                PreviewGroup.Credits,
            ];
        } else {
            groups = [
                PreviewGroup.Metadata,
                PreviewGroup.Linear,
                PreviewGroup.CriticRating,
                PreviewGroup.RecordingStatus,
            ];
        }

        const requestParams: IRecordingPreviewParams = {
            recordingId: this.mRecordingId,
            applicationFeatureArea: APPLICATION_FEATURE_AREA.PLAYER,
            channelNumber: '',
            groups,
        };
        return new Promise((resolve, reject) => {
            this.mServices
                .fetchRecordingPreview(requestParams)
                .then((response: TOfferPreview) => {
                    this.mMetadata.offerPreview = response.offerPreview;
                    resolve();
                })
                .catch((error) => {
                    this.mError = error;
                    reject(error);
                });
        });
    }

    public getMetadata(): IMetadata {
        return this.mMetadata;
    }

    public getError(): LiveTvError {
        return this.mError;
    }

    public prepareMetadataByType(playbackType: {
        metadataType:
            | MetadataType.ACTION_LIST
            | MetadataType.PREVIEW
            | MetadataType.FULL_INFO_PREVIEW
            | MetadataType.NDVR_LAST_VIEWED_POSITION
            | MetadataType.RECORDING_ACTION_LIST
            | MetadataType.FULL_INFO_PREVIEW
            | MetadataType.DELETE_RECORDING;
        ndvrPlaybackModel: INdvrPlaybackModel;
    }): Promise<void> {
        const { metadataType, ndvrPlaybackModel } = playbackType;
        switch (metadataType) {
            case MetadataType.ACTION_LIST:
                return this.retrieveActionList(ndvrPlaybackModel);
            case MetadataType.DELETE_RECORDING:
                return this.deleteRecording();
            case MetadataType.FULL_INFO_PREVIEW:
                return this.fetchRecordingOfferPreviewData(ScreenType.FullBanner);
            case MetadataType.NDVR_LAST_VIEWED_POSITION:
                return this.fetchRecordingLastViewedPosition();
            case MetadataType.PREVIEW:
                return this.fetchRecordingOfferPreviewData(ScreenType.StandardBanner);
            case MetadataType.RECORDING_ACTION_LIST:
                return this.fetchRecordingAction();
            default:
                return Promise.resolve();
        }
    }
}
